import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutlined";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Skeleton from "@mui/material/Skeleton";
import { blue, deepPurple, teal } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import type { CSSObject, Theme } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import { MarketplaceResultType } from "config";
import {
  MARKETPLACE_RESULT_AVATAR_SIZE,
  MARKETPLACE_RESULT_TWO_COLUMN_WIDTH,
  marketplaceResultContainment,
  marketplaceResultIsPhone,
  marketplaceResultNarrowerThan,
} from "marketplace/_views/marketplace-result-card-layout";
import { getResultInitials, splitFactList } from "marketplace/_views/result-text";
import React, { useEffect, useId, useRef, useState } from "react";

/**
 * The one layout every marketplace result is drawn in. Before #5409 the individual, company and team
 * cards each declared their own copy of some fifteen styled components, and the copies had drifted:
 * only two of them had a compact variant, and each sized itself from the window. The three result
 * components now supply only their content.
 *
 * Full width: the avatar in a column of its own, beside a header (type, name, one line of details,
 * and the bookmark), a two-line description and a grid of facts; the footer runs under both. On a
 * phone-width card the avatar shrinks into the header row and everything below it takes the full
 * width, one fact to a row. The compact card — the vendor-team picker in a narrow pane — is drawn
 * like the phone card, with no description and at most two facts. A compact card narrower than a
 * phone — the 500px side panel, the fee-schedule drawer, a dialog on a phone — slims further to the
 * avatar, the name, the first item of the details line and the picker's button (#5817): QA found the
 * panel's cards too crowded to scan. The full profile stays one click away, on the name or the avatar.
 *
 * A full-width card clamps a long details item and the description to two lines each, with a
 * "View more" button when, and only when, the text was cut (#5927). Individuals write a whole
 * biography into their credentials, which drew a nine-line header on a phone. The compact and slim
 * cards keep their own rules above and draw no button.
 */

/**
 * A compact card shows the header, this many facts, and the picker's button. Agreed on #5401. A slim
 * card shows none of them — see `slimOnly`.
 */
export const MARKETPLACE_RESULT_COMPACT_FACTS = 2;

/** How many lines a details item or the description shows on a full-width card until it is expanded. */
export const MARKETPLACE_RESULT_CLAMPED_LINES = 2;

/** How many items of a list-valued fact are named before the rest become a "+N". */
export const MARKETPLACE_RESULT_LISTED_ITEMS = 2;

export type MarketplaceResultFact = {
  label: string;
  value: React.ReactNode;
};

type MarketplaceResultCardProps = {
  resultType: MarketplaceResultType;
  /** The result's name as text: the card's accessible name. */
  label: string;
  avatar: React.ReactNode;
  /** The name, usually a link to the profile. Drawn inside the card's h5. */
  name: React.ReactNode;
  /** One line under the name. Empty entries are left out, and the rest are separated by dots. */
  details?: React.ReactNode[];
  /** Beside the header on the right: the bookmark. */
  headerAction?: React.ReactNode;
  description?: string | null;
  facts?: MarketplaceResultFact[];
  compact?: boolean;
  /** The vendor-team picker's button and its reason. Drawn instead of the footer. */
  picker?: React.ReactNode;
  footer?: React.ReactNode;
};

const TYPE_LABELS: Record<MarketplaceResultType, { label: string; icon: React.ReactNode }> = {
  [MarketplaceResultType.Company]: { label: "Company", icon: <ApartmentOutlinedIcon fontSize="inherit" /> },
  [MarketplaceResultType.Individual]: { label: "Individual", icon: <PersonOutlineIcon fontSize="inherit" /> },
  [MarketplaceResultType.Team]: { label: "Team", icon: <GroupsOutlinedIcon fontSize="inherit" /> },
};

export default function MarketplaceResultCard(props: Readonly<MarketplaceResultCardProps>) {
  const { resultType, label, avatar, name, headerAction, description, compact, picker, footer } = props;
  const details = (props.details ?? []).filter(Boolean);
  const allFacts = props.facts ?? [];
  const facts = compact ? allFacts.slice(0, MARKETPLACE_RESULT_COMPACT_FACTS) : allFacts;
  const typeLabel = TYPE_LABELS[resultType];

  return (
    <Root component="article" aria-label={label} variant="outlined" compact={compact}>
      <Layout compact={compact}>
        <AvatarSlot>{avatar}</AvatarSlot>
        <Header>
          <TitleBlock>
            <TypeLabel data-result-type={resultType} compact={compact}>
              {typeLabel.icon}
              {typeLabel.label}
            </TypeLabel>
            <Name compact={compact}>
              {name}
            </Name>
            {details.length > 0 && (
              <DetailsLine compact={compact}>
                {details.map((detail, index) =>
                  !compact && typeof detail === "string" ? (
                    <ExpandableDetail key={index} text={detail} />
                  ) : (
                    <DetailItem key={index}>{detail}</DetailItem>
                  ),
                )}
              </DetailsLine>
            )}
          </TitleBlock>
          {headerAction}
        </Header>
        <Body compact={compact}>
          {!compact && description?.trim() && <ExpandableDescription text={description} />}
          {facts.length > 0 && (
            <Facts compact={compact}>
              {facts.map((fact) => (
                <Fact key={fact.label} compact={compact}>
                  <FactLabel>{fact.label}</FactLabel>
                  <FactValue>{typeof fact.value === "string" ? <FactText>{fact.value}</FactText> : fact.value}</FactValue>
                </Fact>
              ))}
            </Facts>
          )}
        </Body>
        {picker && <Picker compact={compact}>{picker}</Picker>}
        {!picker && footer && <Footer>{footer}</Footer>}
      </Layout>
    </Root>
  );
}

/**
 * Whether a clamped element cut its text. Measured only while it is clamped - expanded, nothing
 * overflows - and again whenever the card's width changes, so turning a phone sideways can take the
 * button away. Waits for a renderer that lays out: jsdom has no ResizeObserver, and draws no button.
 */
function useClampOverflow<T extends HTMLElement>(text: string, expanded: boolean) {
  const ref = useRef<T>(null);
  const [overflows, setOverflows] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (!element || expanded || typeof ResizeObserver === "undefined") return;
    // Reports once on `observe`, and again on every resize. Keyed on the text as well, because new
    // text in a clamped box can overflow without changing the box's size.
    const observer = new ResizeObserver(() => setOverflows(element.scrollHeight > element.clientHeight));
    observer.observe(element);
    return () => observer.disconnect();
  }, [text, expanded]);
  return { ref, overflows };
}

/**
 * A clamped text and its toggle, both on the text's own lines. Cut, "View more" sits at the end of
 * the last line shown, straight after the ellipsis; open, "View less" follows the last word.
 *
 * A line clamp hides whatever comes after the cut, so the button cannot simply follow the text while
 * it is cut. An invisible copy of its label floats to the end of the last line instead - pushed down
 * there by the clamp's `::before`, see `clampedLines` - so the text wraps around it and the ellipsis
 * lands beside it. The real button is laid over that room. It stays after the text in the DOM, so a
 * screen reader hears the text first and the button second.
 */
function useExpandableText<T extends HTMLElement>(text: string) {
  const id = useId();
  const [expanded, setExpanded] = useState(false);
  const { ref, overflows } = useClampOverflow<T>(text, expanded);

  const toggle = (
    <ToggleButton
      variant="text"
      size="small"
      overLastLine={!expanded}
      aria-expanded={expanded}
      aria-controls={id}
      onClick={() => setExpanded(!expanded)}
    >
      {expanded ? "View less" : "View more"}
    </ToggleButton>
  );

  return {
    id,
    ref,
    expanded,
    /** Leading the text: the room the button takes on the last line. */
    room: overflows && !expanded && <ToggleRoom aria-hidden="true">View more</ToggleRoom>,
    /** Inside the text, after its last word. */
    inlineToggle: expanded && toggle,
    /** After the text, over the room. */
    overlaidToggle: overflows && !expanded && toggle,
  };
}

function ExpandableDetail(props: Readonly<{ text: string }>) {
  const { text } = props;
  const { id, ref, expanded, room, inlineToggle, overlaidToggle } = useExpandableText<HTMLSpanElement>(text);

  return (
    <ExpandableDetailItem>
      <DetailText ref={ref} id={id} expanded={expanded}>
        {room}
        {text}
        {inlineToggle}
      </DetailText>
      {overlaidToggle}
    </ExpandableDetailItem>
  );
}

function ExpandableDescription(props: Readonly<{ text: string }>) {
  const { text } = props;
  const { id, ref, expanded, room, inlineToggle, overlaidToggle } = useExpandableText<HTMLParagraphElement>(text);

  return (
    <DescriptionBlock>
      <Description ref={ref} id={id} expanded={expanded}>
        {room}
        {text}
        {inlineToggle}
      </Description>
      {overlaidToggle}
    </DescriptionBlock>
  );
}

/** How many facts a loading card draws: one row of the full-width grid, or what a compact card shows. */
const SKELETON_FACTS = 3;

/**
 * A result card while the search runs: the same grid, avatar sizes and container queries as the card
 * itself, so the page does not jump when the results arrive. It is decoration; the list that draws it
 * says "Loading" for a screen reader.
 */
export function MarketplaceResultCardSkeleton(props: Readonly<{ compact?: boolean }>) {
  const { compact } = props;
  const factCount = compact ? MARKETPLACE_RESULT_COMPACT_FACTS : SKELETON_FACTS;

  return (
    <Root component="div" variant="outlined" compact={compact} aria-hidden="true" data-testid="result-card-skeleton">
      <Layout compact={compact}>
        <AvatarSlot>
          <AvatarSkeleton variant="circular" compact={compact} />
        </AvatarSlot>
        <Header>
          <TitleBlock>
            <TypeLabelSkeleton variant="text" width={72} height={16} compact={compact} />
            <Skeleton variant="text" width="45%" height={compact ? 22 : 28} />
            <Skeleton variant="text" width="65%" height={20} />
          </TitleBlock>
          {!compact && <Skeleton variant="circular" width={24} height={24} sx={{ flexShrink: 0, margin: 1 }} />}
        </Header>
        <Body compact={compact}>
          {!compact && (
            <div>
              <Skeleton variant="text" height={20} />
              <Skeleton variant="text" width="70%" height={20} />
            </div>
          )}
          <Facts compact={compact}>
            {Array.from({ length: factCount }, (_, index) => (
              <Fact key={index} compact={compact}>
                <Skeleton variant="text" width="45%" height={16} />
                <Skeleton variant="text" width="75%" height={20} />
              </Fact>
            ))}
          </Facts>
        </Body>
        {compact ? (
          <Picker compact>
            <Skeleton variant="rounded" height={36} />
          </Picker>
        ) : (
          <Footer>
            <SkeletonFooterRow>
              <Skeleton className="status" variant="text" width={110} height={20} />
              <Skeleton className="secondary" variant="rounded" width={150} height={36} />
              <Skeleton className="primary" variant="rounded" width={190} height={36} />
              <Skeleton className="more" variant="circular" width={36} height={36} />
            </SkeletonFooterRow>
          </Footer>
        )}
      </Layout>
    </Root>
  );
}

type MarketplaceResultAvatarProps = {
  resultType: MarketplaceResultType;
  name: string;
  src?: string;
  compact?: boolean;
};

/**
 * A fixed 88px at full width, 56px on a phone and 48px compact, however long the name beside it.
 * People are round; companies and teams are rounded squares. With no picture it shows the result's
 * initials on a tint of the type's colour rather than a grey silhouette, so a page of results without
 * pictures can still be told apart.
 */
export function MarketplaceResultAvatar(props: Readonly<MarketplaceResultAvatarProps>) {
  const { resultType, name, src, compact } = props;

  return (
    <ResultAvatar
      src={src || undefined}
      alt=""
      variant={resultType === MarketplaceResultType.Individual ? "circular" : "rounded"}
      resultType={resultType}
      compact={compact}
    >
      {getResultInitials(name)}
    </ResultAvatar>
  );
}

type MarketplaceResultFactListProps = {
  items: readonly (string | null | undefined)[];
};

/**
 * A list-valued fact on one line: the first items, cut off with an ellipsis if they still do not fit,
 * then "+N" for the rest. The whole list is in the tooltip and read out in full by a screen reader,
 * which would otherwise hear "Patent prosecution, Opinions, plus 2" and learn nothing about the 2.
 */
export function MarketplaceResultFactList(props: Readonly<MarketplaceResultFactListProps>) {
  const { listed, hiddenCount } = splitFactList(props.items, MARKETPLACE_RESULT_LISTED_ITEMS);
  const everything = splitFactList(props.items, Number.POSITIVE_INFINITY).listed.join(", ");

  return (
    <>
      <FactText aria-hidden="true" title={everything}>
        {listed.join(", ")}
      </FactText>
      {hiddenCount > 0 && <MoreCount aria-hidden="true">+{hiddenCount}</MoreCount>}
      <span style={visuallyHidden}>{everything}</span>
    </>
  );
}

/** The "+N" beside a list that did not fit. */
export const MoreCount = styled("span")(({ theme }) => ({
  ...theme.typography.caption,
  backgroundColor: theme.palette.grey[200],
  borderRadius: theme.spacing(1.25),
  color: theme.palette.text.secondary,
  flexShrink: 0,
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: "20px",
  padding: theme.spacing(0, 0.75),
}));

// #region Styles

const shouldForwardCompact = (prop: PropertyKey) => prop !== "compact";

/**
 * What a phone-width card changes. The compact card is drawn this way at any width: it is the picker,
 * and the picker's pane is narrower than 1200px by definition, not narrower than a phone.
 */
function narrowOr(compact: boolean | undefined, narrow: CSSObject): CSSObject {
  return compact ? narrow : { [marketplaceResultIsPhone()]: narrow };
}

/**
 * What a slim card changes: a compact card narrower than a phone. It measures the card rather than
 * taking a prop from the picker's host, so every pane that narrow slims alike and a dialog that has
 * the width keeps the facts. A full-width card on a phone keeps everything; it is not choosing a
 * member from a list beside other work.
 */
function slimOnly(compact: boolean | undefined, slim: CSSObject): CSSObject {
  return compact ? { [marketplaceResultIsPhone()]: slim } : {};
}

const hidden: CSSObject = { display: "none" };

const shouldForwardExpanded = (prop: PropertyKey) => prop !== "expanded";

/** The details line and the description both set text on this line height. */
const TEXT_LINE_HEIGHT = 20;

/**
 * Cut to `MARKETPLACE_RESULT_CLAMPED_LINES` lines with an ellipsis, or drawn whole once expanded.
 *
 * While cut, the `::before` is an empty float as tall as every line but the last, so the
 * `ToggleRoom` that clears it lands on the last line, at its end. Its height is a percentage of the
 * text's, which resolves because the text is a stretched flex item - hence the flex wrappers.
 */
function clampedLines(expanded: boolean): CSSObject {
  if (expanded) return { display: "block" };
  return {
    display: "-webkit-box",
    overflow: "hidden",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: MARKETPLACE_RESULT_CLAMPED_LINES,
    "&::before": {
      content: '""',
      float: "right",
      height: `calc(100% - ${TEXT_LINE_HEIGHT}px)`,
    },
  };
}

/** What the toggle's label looks like, so the room it leaves and the button over it are one width. */
function toggleLabel(theme: Theme): CSSObject {
  return {
    ...theme.typography.body2,
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: `${TEXT_LINE_HEIGHT}px`,
  };
}

const Root = styled(Card, { shouldForwardProp: shouldForwardCompact })<{ compact?: boolean }>(
  ({ theme, compact }) => ({
    ...marketplaceResultContainment,
    borderColor: theme.palette.divider,
    borderRadius: theme.spacing(1),
    marginBottom: compact ? theme.spacing(1.5) : theme.spacing(2),
    marginTop: compact ? theme.spacing(1.5) : theme.spacing(2),
    overflow: "visible",
    "&:first-of-type": {
      marginTop: 0,
    },
  }),
) as unknown as React.ComponentType<
  React.ComponentProps<typeof Card> & { component: React.ElementType; compact?: boolean }
>;

const Layout = styled("div", { shouldForwardProp: shouldForwardCompact })<{ compact?: boolean }>(
  ({ theme, compact }) => ({
    columnGap: theme.spacing(2.5),
    display: "grid",
    gridTemplateAreas: `"avatar header" "avatar body" "footer footer"`,
    gridTemplateColumns: `${MARKETPLACE_RESULT_AVATAR_SIZE.full}px minmax(0, 1fr)`,
    padding: theme.spacing(2.5),
    rowGap: theme.spacing(1.25),
    ...narrowOr(compact, {
      columnGap: theme.spacing(1.5),
      gridTemplateAreas: `"avatar header" "body body" "footer footer"`,
      gridTemplateColumns: `${compact ? MARKETPLACE_RESULT_AVATAR_SIZE.compact : MARKETPLACE_RESULT_AVATAR_SIZE.phone}px minmax(0, 1fr)`,
      padding: compact ? theme.spacing(1.5) : theme.spacing(2),
    }),
  }),
);

const AvatarSlot = styled("div")(() => ({
  alignSelf: "start",
  gridArea: "avatar",
  [marketplaceResultIsPhone()]: {
    alignSelf: "center",
  },
}));

const Header = styled("div")(({ theme }) => ({
  alignItems: "flex-start",
  display: "flex",
  gap: theme.spacing(1.5),
  gridArea: "header",
  minWidth: 0,
}));

const TitleBlock = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  gap: 2,
  minWidth: 0,
}));

/**
 * Replaces the dashed type chip, which was #33c (or #CA0, or #0a0) at 66% opacity and failed
 * contrast. Secondary text passes at this size.
 */
const TypeLabel = styled("span", { shouldForwardProp: shouldForwardCompact })<{ compact?: boolean }>(
  ({ theme, compact }) => ({
    ...theme.typography.caption,
    alignItems: "center",
    color: theme.palette.text.secondary,
    display: "inline-flex",
    fontSize: 12,
    fontWeight: theme.typography.fontWeightMedium,
    gap: theme.spacing(0.75),
    letterSpacing: 0.8,
    lineHeight: "16px",
    textTransform: "uppercase",
    "& svg": {
      fontSize: 14,
    },
    ...slimOnly(compact, hidden),
  }),
);

const TypeLabelSkeleton = styled(Skeleton, { shouldForwardProp: shouldForwardCompact })<{ compact?: boolean }>(
  ({ compact }) => slimOnly(compact, hidden),
);

/**
 * A plain h5 rather than Typography's h5 variant: the application's theme gives every variant
 * responsive font sizes, as media queries on the window, and those outrank a size set here - so a
 * 440px picker card drew its name at the 24px a wide window asks for.
 */
const Name = styled("h5", { shouldForwardProp: shouldForwardCompact })<{ compact?: boolean }>(
  ({ theme, compact }) => ({
    fontFamily: theme.typography.fontFamily,
    fontSize: compact ? 16 : 20,
    margin: 0,
    fontWeight: 600,
    lineHeight: 1.3,
    overflowWrap: "anywhere",
    ...narrowOr(compact, { fontSize: compact ? 16 : 18 }),
    "& a": {
      color: theme.palette.text.primary,
    },
  }),
);

/**
 * A slim card keeps the first item: credentials, or else the firm, for a person; the first firm of a
 * team; a company's location. Empty entries were left out already, so the first is never blank.
 */
const DetailsLine = styled("div", { shouldForwardProp: shouldForwardCompact })<{ compact?: boolean }>(
  ({ theme, compact }) => ({
    ...theme.typography.body2,
    color: theme.palette.text.secondary,
    columnGap: theme.spacing(1),
    display: "flex",
    flexWrap: "wrap",
    lineHeight: "20px",
    minWidth: 0,
    ...slimOnly(compact, {
      "& > :not(:first-of-type)": hidden,
      // Doubled to outrank the dot rule on the item itself, which Emotion inserts after this one.
      "&& > :first-of-type::after": { content: "none" },
    }),
  }),
);

/**
 * The dot trails each item rather than leading the next, so a line that wraps on a phone ends on a
 * dot instead of starting the next line with one. Inline-block keeps a linked item's underline and
 * colour off it.
 */
function detailDot(theme: Theme): CSSObject {
  return {
    color: theme.palette.text.disabled,
    content: '"·"',
    display: "inline-block",
    marginLeft: theme.spacing(1),
  };
}

const DetailItem = styled("span")(({ theme }) => ({
  minWidth: 0,
  overflowWrap: "anywhere",
  "&:not(:last-of-type)::after": detailDot(theme),
}));

/**
 * A text item that may be clamped, and the button laid over its last line. A span like `DetailItem`,
 * so the `:first-of-type` and `:last-of-type` rules count both alike. The dot goes on the text,
 * inside the clamp, so a cut item ends on its ellipsis rather than on a dot.
 */
const ExpandableDetailItem = styled("span")(({ theme }) => ({
  display: "flex",
  minWidth: 0,
  position: "relative",
  "&:not(:last-of-type) > :first-child::after": detailDot(theme),
}));

const DetailText = styled("span", { shouldForwardProp: shouldForwardExpanded })<{ expanded: boolean }>(
  ({ expanded }) => ({
    ...clampedLines(expanded),
    minWidth: 0,
    overflowWrap: "anywhere",
  }),
);

/** Hidden whole on a slim card, rather than its facts alone, so the grid keeps no empty row's gap. */
const Body = styled("div", { shouldForwardProp: shouldForwardCompact })<{ compact?: boolean }>(
  ({ theme, compact }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.25),
    gridArea: "body",
    minWidth: 0,
    "&:empty": {
      display: "none",
    },
    ...slimOnly(compact, hidden),
  }),
);

/** The description and the button laid over its last line. */
const DescriptionBlock = styled("div")(() => ({
  display: "flex",
  minWidth: 0,
  position: "relative",
}));

const Description = styled("p", { shouldForwardProp: shouldForwardExpanded })<{ expanded: boolean }>(
  ({ theme, expanded }) => ({
    ...theme.typography.body2,
    ...clampedLines(expanded),
    lineHeight: `${TEXT_LINE_HEIGHT}px`,
    margin: 0,
    minWidth: 0,
    overflowWrap: "anywhere",
  }),
);

/** The room "View more" takes at the end of a cut text's last line: its label, invisible. */
const ToggleRoom = styled("span")(({ theme }) => ({
  ...toggleLabel(theme),
  clear: "both",
  float: "right",
  marginLeft: theme.spacing(0.5),
  visibility: "hidden",
}));

/**
 * A text button set in the text's own line. Over the last line of a cut text it sits on the
 * `ToggleRoom`; after the last word of an opened one it follows the text like another word.
 */
const ToggleButton = styled(Button, { shouldForwardProp: (prop) => prop !== "overLastLine" })<{
  overLastLine: boolean;
}>(({ theme, overLastLine }) => ({
  ...toggleLabel(theme),
  minWidth: 0,
  padding: 0,
  textTransform: "none",
  verticalAlign: "baseline",
  ...(overLastLine
    ? { bottom: 0, position: "absolute", right: 0 }
    : { marginLeft: theme.spacing(0.5) }),
}));

/**
 * Three columns, two below 840px, one on a phone. A compact card is one column at any width, and
 * draws none of the width rules: a container query outranks a plain declaration beside it, so the
 * two-column rule would win in the picker's 440-500px pane.
 */
const Facts = styled("div", { shouldForwardProp: shouldForwardCompact })<{ compact?: boolean }>(
  ({ theme, compact }) => {
    const oneColumn = { gap: theme.spacing(0.75), gridTemplateColumns: "minmax(0, 1fr)" };
    if (compact) return { display: "grid", ...oneColumn };
    return {
      display: "grid",
      gap: theme.spacing(1.5, 3),
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      [marketplaceResultNarrowerThan(MARKETPLACE_RESULT_TWO_COLUMN_WIDTH)]: {
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      },
      [marketplaceResultIsPhone()]: oneColumn,
    };
  },
);

const Fact = styled("div", { shouldForwardProp: shouldForwardCompact })<{ compact?: boolean }>(
  ({ theme, compact }) => ({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 0,
    ...narrowOr(compact, {
      alignItems: "center",
      columnGap: theme.spacing(1.5),
      display: "grid",
      // Wide enough for "Technical background" on one line.
      gridTemplateColumns: `${theme.spacing(17)} minmax(0, 1fr)`,
    }),
  }),
);

const FactLabel = styled("span")(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
  lineHeight: "16px",
}));

const FactValue = styled("div")(({ theme }) => ({
  ...theme.typography.body2,
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(0.75),
  lineHeight: "20px",
  minWidth: 0,
  whiteSpace: "nowrap",
}));

/** One line, cut off with an ellipsis rather than wrapped. */
export const FactText = styled("span")(() => ({
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const Picker = styled("div", { shouldForwardProp: shouldForwardCompact })<{ compact?: boolean }>(
  ({ compact }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: compact ? "stretch" : "flex-end",
    gridArea: "footer",
    minWidth: 0,
    [marketplaceResultIsPhone()]: {
      alignItems: "stretch",
    },
  }),
);

const Footer = styled("div")(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  gridArea: "footer",
  marginTop: theme.spacing(0.75),
  minWidth: 0,
  paddingTop: theme.spacing(1.5),
}));

type ResultAvatarStyleProps = { resultType: MarketplaceResultType; compact?: boolean };

const AVATAR_TINTS: Record<MarketplaceResultType, { background: string; color: string }> = {
  [MarketplaceResultType.Company]: { background: deepPurple[50], color: deepPurple[800] },
  [MarketplaceResultType.Individual]: { background: blue[50], color: blue[900] },
  [MarketplaceResultType.Team]: { background: teal[50], color: teal[800] },
};

function avatarSize(theme: Theme, size: number): CSSObject {
  return {
    fontSize: Math.round(size * 0.36),
    height: size,
    width: size,
    "&.MuiAvatar-rounded": {
      borderRadius: theme.spacing(size >= MARKETPLACE_RESULT_AVATAR_SIZE.full ? 1.5 : 1),
    },
  };
}

const ResultAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== "compact" && prop !== "resultType",
})<ResultAvatarStyleProps>(({ theme, resultType, compact }) => ({
  ...avatarSize(theme, compact ? MARKETPLACE_RESULT_AVATAR_SIZE.compact : MARKETPLACE_RESULT_AVATAR_SIZE.full),
  backgroundColor: AVATAR_TINTS[resultType].background,
  color: AVATAR_TINTS[resultType].color,
  cursor: "pointer",
  fontWeight: theme.typography.fontWeightMedium,
  letterSpacing: 0.5,
  ...(compact ? {} : { [marketplaceResultIsPhone()]: avatarSize(theme, MARKETPLACE_RESULT_AVATAR_SIZE.phone) }),
}));

const AvatarSkeleton = styled(Skeleton, { shouldForwardProp: shouldForwardCompact })<{ compact?: boolean }>(
  ({ compact }) => {
    const size = compact ? MARKETPLACE_RESULT_AVATAR_SIZE.compact : MARKETPLACE_RESULT_AVATAR_SIZE.full;
    const phone = MARKETPLACE_RESULT_AVATAR_SIZE.phone;
    return {
      height: size,
      width: size,
      ...(compact ? {} : { [marketplaceResultIsPhone()]: { height: phone, width: phone } }),
    };
  },
);

/** The footer's shape: status on the left, the buttons on the right; on a phone, the primary on its own row. */
const SkeletonFooterRow = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  justifyContent: "flex-end",
  "& > .status": { marginRight: "auto" },
  "& > .more": { flexShrink: 0 },
  [marketplaceResultIsPhone()]: {
    "& > .primary": { flexBasis: "100%", order: 1 },
    "& > .secondary": { flexGrow: 1, order: 2 },
    "& > .more": { order: 3 },
    "& > .status": { order: 4 },
  },
}));

// #endregion
